import { useEffect, useRef } from 'react';
import hammer from '@/assets/brand/recepthor-hammer-256.png';
import { bankName } from '@/lib/chileanBanks.js';
import {
  formatDeadlineDate,
  formatDeadlineUrgencyBadge,
} from '@/lib/dateUtils.js';
import Button from '@/ui/Button/Button.jsx';
import './Oferta.css';
import OfferForm from './OfferForm.jsx';
import OfferBreakdownList from '../solicitudes/OfferBreakdownList.jsx';
import { offerBreakdownLines } from '@/lib/priceBreakdown.js';
import {
  actuationLabel,
  addressTypeLabel,
  conservadorFields,
  recipientLines,
} from '@/lib/taskActuation.js';

const messages = {
  loading: 'Cargando solicitud…',
  incomplete:
    'Este link está incompleto. Abre de nuevo el link que te llegó por correo.',
  invalid: 'Este link no es válido. Abre de nuevo el link del correo.',
  expired: 'El plazo para enviar una oferta ya pasó.',
  declined: 'Ya rechazaste esta solicitud.',
  closed: 'Esta solicitud ya no recibe ofertas.',
  limited: 'Hay demasiados intentos. Prueba de nuevo en unos minutos.',
  error: 'No pudimos cargar la solicitud. Inténtalo de nuevo.',
};

// What happens after the offer, in the words of the hub (/receptores).
const STEPS = [
  [
    'Envías honorarios y fecha',
    'Tú fijas el monto: no hay tarifa impuesta por la plataforma.',
  ],
  [
    'El estudio compara y elige',
    'Ve las ofertas en una sola pantalla y paga por la pasarela de pago.',
  ],
  [
    'Cumples la diligencia',
    'Con la ficha de la causa a la vista; el acta se cierra con foto, georreferencia y hora.',
  ],
  [
    'Recepthor te transfiere',
    'Los honorarios llegan íntegros: Recepthor no descuenta comisión. El fee lo paga el estudio, aparte.',
  ],
];
const ABOUT =
  'Recepthor es una plataforma donde los estudios publican diligencias judiciales y los receptores del listado oficial del Poder Judicial responden con honorarios y una fecha comprometida. Gratis para el receptor, sin tarjeta y sin contrato.';

// The badge ("50d", "4h", "12 min") split into figure and unit, so the figure
// can be the big element. "Mañana" and "Vencida" stay as they are.
function countdown(badge) {
  const match = /^(\d+)\s?(d|h|min)$/.exec(badge ?? '');
  return match ? { n: match[1], unit: match[2] } : null;
}

const icon = {
  calendar: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  ),
  pin: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 21s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  ),
  people: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.5a3.5 3.5 0 0 1 0 7M21.5 20a6.5 6.5 0 0 0-4-6" />
    </svg>
  ),
  file: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4M9 12h6M9 16h6" />
    </svg>
  ),
  note: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 5h16v11H9l-5 4z" />
    </svg>
  ),
  chevron: (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3 6l5 5 5-5" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12l5 5 9-10" />
    </svg>
  ),
};

function Fact({ glyph, label, children }) {
  return (
    <li className="oferta-fact">
      <span className="oferta-fact__icon">{icon[glyph]}</span>
      <div>
        <div className="oferta-fact__k">{label}</div>
        <div className="oferta-fact__v">{children}</div>
      </div>
    </li>
  );
}

function Rows({ title, rows }) {
  const filled = rows.filter(([, value]) => value);
  if (!filled.length) return null;
  return (
    <section className="oferta-group">
      <h3>{title}</h3>
      <dl className="oferta-rows">
        {filled.map(([label, value]) => (
          <div className="oferta-row" key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Steps({ from = 0 }) {
  return (
    <ol className="oferta-steps">
      {STEPS.slice(from).map(([title, body], i) => (
        <li key={title} className={i === 0 && from === 0 ? 'is-now' : ''}>
          <span className="oferta-steps__n">{from + i + 1}</span>
          <span>
            <b>{title}</b>
            {body}
          </span>
        </li>
      ))}
    </ol>
  );
}

export default function OfertaView({
  model,
  activation = 'idle',
  onResend,
  onRetry,
  onSubmit,
  notice,
  formErrors,
}) {
  const main = useRef(null);
  useEffect(() => {
    main.current?.focus({ preventScroll: true });
  }, [model.state]);
  const { state, quote } = model;
  const request = quote?.request;
  const payout = quote?.receptor?.payoutAccount;
  const urgency =
    request &&
    formatDeadlineUrgencyBadge(
      request.taskDeadline,
      Date.now(),
      request.taskDeadlineExpiresAt,
    );
  const parts = countdown(urgency);
  const deadline = request && formatDeadlineDate(request.taskDeadline);
  const recipients = recipientLines(request);
  const actuation = request && actuationLabel(request);
  const addressType = request && addressTypeLabel(request.addressType);
  const error = [
    'incomplete',
    'invalid',
    'expired',
    'limited',
    'error',
  ].includes(state);
  const open = state === 'open' && request;

  const payoutLine = payout && (state === 'open' || state === 'offered') && (
    /* Before the offer the default account decides, and the profile changes
       it. An offer already made is paid to the account it was made with,
       which the profile no longer changes. A declined or closed
       request has nothing to pay. */
    <section className="oferta-payout" aria-label="Cuenta de pago">
      <p>
        Se te pagará a la cuenta {bankName(payout.bankCode)} terminada en{' '}
        {payout.last4}.{state === 'open' && ' Se cambia en tu perfil.'}
      </p>
    </section>
  );
  const activationBlock = quote?.receptor?.accountActivated === false && (
    <section className="oferta-block" aria-label="Activación de cuenta">
      <h2>Activa tu cuenta</h2>
      <p>Revisa tu correo para activar tu cuenta en Recepthor.</p>
      {activation === 'sent' ? (
        <p role="status" className="oferta-alert oferta-alert--ok">
          Correo de activación enviado. Revisa tu bandeja de entrada.
        </p>
      ) : (
        <Button
          onClick={onResend}
          disabled={activation === 'sending'}
          variant="secondary"
        >
          {activation === 'sending'
            ? 'Enviando…'
            : 'Reenviar correo de activación'}
        </Button>
      )}
      {activation === 'error' && (
        <p role="alert" className="oferta-alert oferta-alert--err">
          No pudimos reenviar el correo. Inténtalo de nuevo.
        </p>
      )}
    </section>
  );
  const reopen = quote && (
    <p className="oferta-reopen">
      Si recargas esta página, abre de nuevo el link del correo.
    </p>
  );

  return (
    <div className="oferta-page">
      <header className="oferta-top">
        <div className="oferta-wrap">
          <span className="oferta-brand" role="img" aria-label="Recepthor">
            <span className="oferta-brand__mark">
              <img src={hammer} alt="" />
            </span>
            <span className="oferta-wm">
              <span>RECEP</span>
              <b>THOR</b>
            </span>
          </span>
        </div>
      </header>
      <main
        ref={main}
        tabIndex={-1}
        className="oferta"
        aria-labelledby="oferta-title"
      >
        {open ? (
          <div className="oferta-wrap oferta-body">
            <div className="oferta-head">
              {actuation && (
                <p className="oferta-kicker">Solicitud de oferta</p>
              )}
              <h1 id="oferta-title" className="oferta-title">
                {actuation || 'Solicitud de oferta'}
              </h1>
              <p className="oferta-lede">
                {request.requesterName
                  ? `${request.requesterName} publicó esta diligencia`
                  : 'Esta diligencia se publicó'}
                {request.tribunalName
                  ? ` y te llega porque actúas ante el ${request.tribunalName}`
                  : ''}
                . Tú fijas tus honorarios y la fecha.
              </p>
              {notice && (
                <p role="alert" className="oferta-alert oferta-alert--err">
                  {notice}
                </p>
              )}
              {(urgency || deadline) && (
                <ul className="oferta-facts" aria-label="Plazo">
                  <Fact glyph="calendar" label="Plazo para notificar">
                    <span
                      className={`oferta-plazo${urgency === 'Vencida' ? ' is-late' : ''}`}
                    >
                      {parts ? (
                        <span className="oferta-plazo__n">
                          {parts.n}
                          <span className="oferta-plazo__u">{parts.unit}</span>
                        </span>
                      ) : (
                        urgency && (
                          <span className="oferta-plazo__n oferta-plazo__n--text">
                            {urgency}
                          </span>
                        )
                      )}
                      {deadline && <span>Notificar antes del {deadline}</span>}
                    </span>
                  </Fact>
                </ul>
              )}
            </div>

            <div className="oferta-side">
              <section className="oferta-card" aria-label="Oferta">
                <h2>Tu oferta</h2>
                <p className="oferta-card__sub">
                  Tus honorarios y la fecha en que puedes cumplir. Tú fijas el
                  monto: no hay tarifa impuesta por la plataforma.
                </p>
                <OfferForm
                  key={quote.quoteId}
                  quote={quote}
                  onSubmit={onSubmit}
                  initialErrors={formErrors}
                />
              </section>
              {payoutLine}
              {activationBlock}
              <p className="oferta-trust">
                {icon.shield}
                Solo receptores del Registro de Receptores del Poder Judicial,
                con su habilitación vigente.
              </p>
            </div>

            <div className="oferta-rest">
              <ul
                className="oferta-facts oferta-facts--rest"
                aria-label="Lugar y destinatarios"
              >
                {(request.address || request.commune) && (
                  <Fact glyph="pin" label="Dónde">
                    {[
                      request.address || request.commune,
                      request.addressDetails,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                    <small>
                      {[
                        request.address && request.commune,
                        request.region,
                        addressType && `domicilio ${addressType.toLowerCase()}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </small>
                  </Fact>
                )}
                {recipients.length > 0 && (
                  <Fact glyph="people" label="A quién">
                    <ul role="list">
                      {recipients.map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  </Fact>
                )}
                {request.rol && (
                  <Fact glyph="file" label="Causa">
                    <span>Rol {request.rol}</span>
                    <small>
                      {[
                        request.tribunalName,
                        request.competence,
                        request.procedure,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </small>
                  </Fact>
                )}
              </ul>

              {request.notes && (
                <section className="oferta-note" aria-label="Notas del abogado">
                  {icon.note}
                  <div>
                    <h2>Notas del abogado</h2>
                    <p>{request.notes}</p>
                  </div>
                </section>
              )}

              <details className="oferta-detail">
                <summary>
                  Detalle completo de la solicitud
                  {icon.chevron}
                </summary>
                <Rows
                  title="Causa"
                  rows={[
                    ['Rol', request.rol],
                    ['Tribunal', request.tribunalName],
                    ['Corte', request.courtName],
                    ['Competencia', request.competence],
                    ['Procedimiento', request.procedure],
                    // The first name of whoever sent the request, not the firm.
                    ['Solicitado por', request.requesterName],
                  ]}
                />
                <Rows
                  title="Diligencia"
                  rows={[
                    ['Actuación', actuation],
                    ...(conservadorFields(request) ?? []),
                    [
                      recipients.length === 1
                        ? 'Destinatario'
                        : 'Destinatarios',
                      recipients.length ? (
                        <ul className="oferta-list" role="list">
                          {recipients.map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                      ) : null,
                    ],
                  ]}
                />
                <Rows
                  title="Lugar"
                  rows={[
                    ['Dirección', request.address],
                    ['Detalle de dirección', request.addressDetails],
                    ['Tipo de domicilio', addressType],
                    ['Comuna', request.commune],
                    ['Región', request.region],
                  ]}
                />
              </details>

              <section className="oferta-after" aria-label="Qué pasa después">
                <h2>Qué pasa después</h2>
                <Steps />
                <p className="oferta-about">{ABOUT}</p>
                {reopen}
              </section>
            </div>
          </div>
        ) : (
          <div className="oferta-wrap oferta-result">
            {state === 'offered' && (
              <span className="oferta-check">{icon.check}</span>
            )}
            <h1 id="oferta-title" className="oferta-title">
              {state === 'offered'
                ? 'Ya enviaste tu oferta'
                : 'Oferta en Recepthor'}
            </h1>
            {messages[state] && (
              <p role={error ? 'alert' : 'status'} className="oferta-status">
                {messages[state]}
              </p>
            )}
            {notice && (
              <p role="alert" className="oferta-alert oferta-alert--err">
                {notice}
              </p>
            )}
            {(state === 'error' || state === 'limited') && (
              <Button onClick={onRetry} variant="secondary">
                Reintentar
              </Button>
            )}
            {state === 'offered' && quote?.offer && (
              <section className="oferta-summary" aria-label="Oferta enviada">
                <p className="oferta-summary__price">
                  Precio:{' '}
                  {new Intl.NumberFormat('es-CL', {
                    style: 'currency',
                    currency: 'CLP',
                  }).format(quote.offer.price)}
                </p>
                <OfferBreakdownList
                  className="oferta-list"
                  lines={offerBreakdownLines(
                    quote.offer.priceBreakdown,
                    request?.recipients,
                  )}
                />
                {/* dateOffered is the chosen day at the end of the UTC day,
                    so it reads in UTC like the deadline. */}
                <p>
                  Fecha ofrecida:{' '}
                  {formatDeadlineDate(quote.offer.dateOffered, 'numeric')}
                </p>
              </section>
            )}
            {payoutLine}
            {activationBlock}
            {state === 'offered' && (
              <section className="oferta-after" aria-label="Qué pasa después">
                <h2>Qué pasa después</h2>
                <Steps from={1} />
              </section>
            )}
            {error && <p className="oferta-about">{ABOUT}</p>}
            {reopen}
          </div>
        )}
      </main>
    </div>
  );
}
